import { useCallback, useEffect, useRef, useState } from 'react';
import { IGeoCoords } from 'types/previewCampaign';

export type GeolocationStatus =
    | 'idle'
    | 'prompting'
    | 'granted'
    // Người dùng tắt/bỏ qua popup: gọi lại getCurrentPosition là popup hiện lại.
    | 'dismissed'
    // Người dùng bấm Chặn: chỉ mở lại được trong cài đặt trình duyệt.
    | 'blocked'
    // GPS lỗi/timeout, quyền vẫn bình thường.
    | 'unavailable'
    | 'unsupported';

const POSITION_OPTIONS: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 0,
};

const queryPermissionState = async (): Promise<PermissionState | null> => {
    if (!navigator.permissions?.query) return null;
    try {
        const result = await navigator.permissions.query({
            name: 'geolocation',
        });
        return result.state;
    } catch {
        return null;
    }
};

export const useGeolocation = () => {
    const [status, setStatus] = useState<GeolocationStatus>('idle');
    const [coords, setCoords] = useState<IGeoCoords | null>(null);
    const [error, setError] = useState('');
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const requestLocation = useCallback(() => {
        if (!('geolocation' in navigator)) {
            setStatus('unsupported');
            setError('Trình duyệt của bạn không hỗ trợ định vị.');
            return;
        }
        // Geolocation chỉ hoạt động trong secure context. Trên HTTP thuần trình
        // duyệt luôn trả lỗi nên báo rõ thay vì để người dùng bấm vô ích.
        if (!window.isSecureContext) {
            setStatus('unsupported');
            setError(
                'Khảo sát cần được mở qua HTTPS thì trình duyệt mới cho phép lấy vị trí.'
            );
            return;
        }

        setStatus('prompting');
        setError('');
        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (!isMounted.current) return;
                setCoords({
                    lat: position.coords.latitude,
                    long: position.coords.longitude,
                });
                setStatus('granted');
            },
            async (positionError) => {
                if (!isMounted.current) return;

                if (positionError.code === positionError.PERMISSION_DENIED) {
                    // getCurrentPosition trả cùng mã lỗi cho "tắt popup" và
                    // "chặn cứng"; chỉ Permissions API phân biệt được. Safari
                    // không hỗ trợ API này -> coi như còn hỏi lại được, vì
                    // Safari vẫn hiện popup ở lần gọi sau.
                    const permission = await queryPermissionState();
                    if (!isMounted.current) return;

                    if (permission === 'denied') {
                        setStatus('blocked');
                        setError(
                            'Quyền vị trí đang bị chặn nên bạn chưa thể tham gia khảo sát.'
                        );
                    } else {
                        setStatus('dismissed');
                        setError(
                            'Bạn cần chia sẻ vị trí thì mới tham gia khảo sát được.'
                        );
                    }
                    return;
                }

                setStatus('unavailable');
                setError(
                    positionError.code === positionError.TIMEOUT
                        ? 'Quá thời gian xác định vị trí. Vui lòng thử lại.'
                        : 'Không xác định được vị trí của bạn. Vui lòng kiểm tra GPS/kết nối mạng rồi thử lại.'
                );
            },
            POSITION_OPTIONS
        );
    }, []);

    // Ở trạng thái blocked không nút nào trong trang gọi lại được popup, nên đây
    // là đường tự phục hồi duy nhất: user bật lại quyền trong cài đặt trình
    // duyệt -> nhận sự kiện -> tự lấy toạ độ, không cần bấm gì.
    useEffect(() => {
        if (!navigator.permissions?.query) return;

        let permissionStatus: PermissionStatus | null = null;
        const handleChange = () => {
            if (permissionStatus?.state === 'granted') {
                requestLocation();
            }
        };

        navigator.permissions
            .query({ name: 'geolocation' })
            .then((result) => {
                permissionStatus = result;
                result.addEventListener('change', handleChange);
            })
            .catch(() => {
                // Không có Permissions API -> mất tính năng tự nhận lại quyền.
            });

        return () => {
            permissionStatus?.removeEventListener('change', handleChange);
        };
    }, [requestLocation]);

    return { status, coords, error, requestLocation };
};

export default useGeolocation;
